import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { Switch } from "@shared/ui/switch";
import { Input } from "@shared/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import {
  Pencil,
  Plus,
  Search,
  Calendar,
  User,
  Check,
  X,
  Receipt,
  Percent,
  TrendingUp,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { useToast } from "@shared/hooks/use-toast";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Alert, AlertDescription } from "@shared/ui/alert";
import {
  useTaxRates,
  useCreateTaxRate,
  useUpdateTaxRate,
  useToggleTaxRateStatus,
  useTaxRatesSearch,
} from "../../../../hooks";
import { TaxRate } from "../../../../types";

// ====================== TYPES ======================

interface TaxFormData {
  name: string;
  rate: string;
  description?: string;
}

// ====================== HOOKS ======================

// Custom hook for debounced search
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// ====================== UTILITY FUNCTIONS ======================

// Validate rate format
const validateRate = (rate: string) => {
  const rateRegex = /^\d+(\.\d{1,2})?%?$/;
  return rateRegex.test(rate);
};

// Get rate category
const getRateCategory = (rate: string) => {
  const numericRate = parseFloat(rate?.replace("%", ""));
  if (numericRate === 0)
    return { category: "Zero", color: "text-gray-600 bg-gray-50" };
  if (numericRate <= 5)
    return { category: "Low", color: "text-green-600 bg-green-50" };
  if (numericRate <= 18)
    return { category: "Standard", color: "text-blue-600 bg-blue-50" };
  return { category: "High", color: "text-red-600 bg-red-50" };
};

// ====================== COMPONENTS ======================

// Tax Rate card component for better reusability
const TaxRateCard: React.FC<{
  rate: TaxRate;
  onEdit: (rate: TaxRate) => void;
  onToggleActive: (rate: TaxRate) => void;
  isToggling?: boolean;
}> = ({ rate, onEdit, onToggleActive, isToggling = false }) => {
  const category = getRateCategory(rate.rate);

  return (
    <Card className="overflow-hidden transition-all hover:shadow-md hover:border-primary/20 group">
      <CardContent className="p-0">
        <div className="p-4 border-b bg-muted/20">
          <div className="flex justify-between items-start">
            <div className="flex items-start gap-2">
              <Receipt className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <h4 className="font-medium">{rate.rate}</h4>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm ">{rate.description}</span>
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(rate)}
              className="opacity-0 group-hover:opacity-100 hover:bg-background/80"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="p-4">
          <div className="flex gap-2 items-center text-sm text-muted-foreground mb-2">
            <User className="h-3.5 w-3.5" />
            <p>{rate.createdBy}</p>
          </div>
          <div className="flex gap-2 items-center text-sm text-muted-foreground mb-4">
            <Calendar className="h-3.5 w-3.5" />
            <p>{rate.createdDate}</p>
          </div>

          <div className="flex items-center justify-between pt-4 border-t">
            <span className="text-sm">
              {rate.isActive ? (
                <span className="flex items-center gap-1.5 text-green-600 bg-green-50 dark:bg-green-950/30 px-2 py-1 rounded-full text-xs font-medium">
                  <Check className="h-3 w-3" /> Active
                </span>
              ) : (
                <span className="flex items-center gap-1.5 text-gray-600 bg-gray-50 dark:bg-gray-800/30 px-2 py-1 rounded-full text-xs font-medium">
                  <X className="h-3 w-3" /> Inactive
                </span>
              )}
            </span>
            <Switch
              checked={rate.isActive}
              onCheckedChange={() => onToggleActive(rate)}
              disabled={isToggling}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Empty state component
const EmptyState: React.FC<{
  hasSearchQuery: boolean;
  onAddNew: () => void;
}> = ({ hasSearchQuery, onAddNew }) => {
  return (
    <div className="col-span-full py-12 text-center text-muted-foreground bg-muted/20 rounded-lg border border-dashed">
      <div className="flex flex-col items-center gap-2">
        <Search className="h-10 w-10 text-muted-foreground/50" />
        <h4>No tax rates found</h4>
        <p className="text-sm">
          {hasSearchQuery
            ? "Try adjusting your search or add a new tax rate"
            : "No tax rates available"}
        </p>
        <Button variant="outline" className="mt-2" onClick={onAddNew}>
          <Plus className="mr-2 h-4 w-4" /> Add New Tax Rate
        </Button>
      </div>
    </div>
  );
};

// ====================== MAIN COMPONENT ======================

const TaxTab: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentTaxRate, setCurrentTaxRate] = useState<TaxRate | null>(null);

  // Get search query from URL
  const searchQuery = searchParams.get("search") || "";

  // Debounce the search input for better UX (but server handles the actual search)
  const [searchInput, setSearchInput] = useState(searchQuery);
  const debouncedSearchInput = useDebounce(searchInput, 500);

  // Update URL when debounced search changes
  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (debouncedSearchInput.trim()) {
      newSearchParams.set("search", debouncedSearchInput.trim());
    } else {
      newSearchParams.delete("search");
    }
    setSearchParams(newSearchParams, { replace: true });
  }, [debouncedSearchInput, searchParams, setSearchParams]);

  // API hooks - now using server-side search
  const { data: taxRates = [] } = useTaxRates();
  const { data: taxRatesSearch = [] } = useTaxRatesSearch(searchQuery);
  const createTaxRateMutation = useCreateTaxRate();
  const updateTaxRateMutation = useUpdateTaxRate();
  const toggleStatusMutation = useToggleTaxRateStatus();

  const taxRatesData = searchQuery ? taxRatesSearch : taxRates;

  // Form fields configuration with custom validation
  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "name",
        type: "decimal",
        label: "Rate (%)",
        placeholder: "18.5",
        required: true,
        helperText: "Enter the rate as a number (e.g., 18 or 18.5)",
        validate: (value: string) => {
          if (!validateRate(value)) {
            return "Please enter a valid rate (e.g., 18, 18.5, or 18%)";
          }
          return true;
        },
      },
      {
        name: "description",
        type: "textarea",
        label: "Description (Optional)",
        placeholder: "Enter description...",
        rows: 3,
        maxLength: 500,

        className: "pt-2",
      },
    ],
    []
  );

  // Create form instance using FormService - following established pattern
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      name: "",
      rate: "",
      description: "",
    },
  });

  // Update form when editing a tax rate - using FormService.populateForm
  useEffect(() => {
    if (currentTaxRate && isEditModalOpen) {
      const populateData = {
        name: currentTaxRate?.name,
        description: currentTaxRate.description || "",
      };
      FormService.populateForm(form, populateData, formFields);
    } else if (isAddModalOpen) {
      const populateData = {
        name: "",
        description: "",
      };
      FormService.populateForm(form, populateData, formFields);
    }
  }, [currentTaxRate, isEditModalOpen, isAddModalOpen, form, formFields]);

  // Handle field changes - simplified like other components
  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    // Optional: Add any side effects here if needed
    // The form already handles the value update internally
  }, []);

  // Handle form submission
  const handleSubmit = async (data: TaxFormData) => {
    try {
      if (currentTaxRate) {
        const updatePayload = {
          code: String(currentTaxRate.code),
          name: String(data.name),
          rate: data.rate,
          description: data.description,
        };
        // Update existing tax rate
        await updateTaxRateMutation.mutateAsync(updatePayload);
        setIsEditModalOpen(false);
      } else {
        const createPayload = {
          name: String(data.name),
          rate: String(data.rate),
          description: data.description,
        };
        // Create new tax rate
        await createTaxRateMutation.mutateAsync(createPayload);
        setIsAddModalOpen(false);
      }

      // Reset form state - following established pattern
      FormService.resetForm(form, formFields);
      setCurrentTaxRate(null);
    } catch (error) {}
  };

  const handleReset = useCallback(() => {
    FormService.resetForm(form, formFields);
  }, [form, formFields]);

  // Event handlers
  const handleEditClick = useCallback((rate: TaxRate) => {
    setCurrentTaxRate(rate);
    setIsEditModalOpen(true);
  }, []);

  const handleToggleActive = useCallback(
    async (rate: TaxRate) => {
      try {
        await toggleStatusMutation.mutateAsync({
          code: rate.code,
          isActive: !rate.isActive,
          name: rate.name,
        });
      } catch (error) {}
    },
    [toggleStatusMutation]
  );

  const handleAddModalClose = useCallback(() => {
    setIsAddModalOpen(false);
    setCurrentTaxRate(null);
    FormService.resetForm(form, formFields);
  }, [form, formFields]);

  const handleEditModalClose = useCallback(() => {
    setIsEditModalOpen(false);
    setCurrentTaxRate(null);
    FormService.resetForm(form, formFields);
  }, [form, formFields]);

  // Handle search input change
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchInput(e.target.value);
    },
    []
  );

  return (
    <div className="">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background pt-2 pb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start flex-col">
          <h3 className="page-subheading">Tax Rates</h3>
          <p className="page-subtitle">Create and manage Tax rates.</p>
        </div>
        <Button
          onClick={() => setIsAddModalOpen(true)}
          disabled={createTaxRateMutation.isPending}
        >
          {createTaxRateMutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Plus className="mr-2 h-4 w-4" />
          )}
          Add New Tax Rate
        </Button>
      </div>

      {/* Grid */}
      <div className="relative pb-4">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search tax rates..."
          className="pl-8 w-full sm:w-[250px]"
          value={searchInput}
          onChange={handleSearchChange}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.isArray(taxRatesData) &&
          taxRatesData.map((rate) => (
            <TaxRateCard
              key={rate.id}
              rate={rate}
              onEdit={handleEditClick}
              onToggleActive={handleToggleActive}
              isToggling={toggleStatusMutation.isPending}
            />
          ))}

        {Array.isArray(taxRatesData) && taxRatesData.length === 0 && (
          <EmptyState
            hasSearchQuery={!!searchQuery}
            onAddNew={() => setIsAddModalOpen(true)}
          />
        )}
      </div>

      {/* Add Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={handleAddModalClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add New Tax Rate</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pr-4">
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSubmit}
              onReset={handleReset}
              onFieldChange={handleFieldChange}
              loading={createTaxRateMutation.isPending}
              config={{
                showResetButton: false,
                showSubmitButton: false,
                resetOnSubmit: false,
                classes: {
                  form: "space-y-4",
                  container: "space-y-2",
                  label: "block text-sm font-medium text-gray-900 mb-1",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed",
                  textarea:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical disabled:bg-gray-100 disabled:cursor-not-allowed",
                  select:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed",
                  error: "text-sm text-red-600 mt-1",
                  helperText: "text-xs text-muted-foreground mt-1",
                  required: "text-red-500 ml-1",
                },
              }}
              renderCustomFooter={() => (
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={handleAddModalClose}
                    disabled={createTaxRateMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={form.handleSubmit(handleSubmit)}
                    disabled={createTaxRateMutation.isPending}
                  >
                    {createTaxRateMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      "Save Tax Rate"
                    )}
                  </Button>
                </div>
              )}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={handleEditModalClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Tax Rate</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pr-4">
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSubmit}
              onReset={handleReset}
              onFieldChange={handleFieldChange}
              loading={updateTaxRateMutation.isPending}
              config={{
                showResetButton: false,
                showSubmitButton: false,
                resetOnSubmit: false,
                classes: {
                  form: "space-y-4",
                  container: "space-y-2",
                  label: "block text-sm font-medium text-gray-900 mb-1",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed",
                  textarea:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical disabled:bg-gray-100 disabled:cursor-not-allowed",
                  select:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed",
                  error: "text-sm text-red-600 mt-1",
                  helperText: "text-xs text-muted-foreground mt-1",
                  required: "text-red-500 ml-1",
                },
              }}
              renderCustomFooter={() => (
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={handleEditModalClose}
                    disabled={updateTaxRateMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={form.handleSubmit(handleSubmit)}
                    disabled={updateTaxRateMutation.isPending}
                  >
                    {updateTaxRateMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      "Update Tax Rate"
                    )}
                  </Button>
                </div>
              )}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaxTab;
